
import React from 'react';
import { OrderStatus } from '../types';

export const Button = ({ children, onClick, variant = 'primary', className = '', type = 'button', disabled = false, size = 'md' }: any) => {
  const baseStyle = "rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed tracking-wide active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-950";
  
  const sizes = {
    sm: "px-3 py-2 text-xs",
    md: "px-6 py-3.5 text-sm", // Taller for touch targets
    lg: "px-8 py-4 text-base"
  };

  const variants = {
    primary: "bg-white text-zinc-950 hover:bg-zinc-200 focus:ring-white shadow-[0_0_20px_rgba(255,255,255,0.15)] border border-transparent",
    secondary: "bg-zinc-800 text-white border border-zinc-700 hover:bg-zinc-700 hover:border-zinc-600 focus:ring-zinc-600 shadow-sm",
    danger: "bg-red-900/20 text-red-400 border border-red-900/50 hover:bg-red-900/40 hover:border-red-500/50 focus:ring-red-500",
    ghost: "text-zinc-400 hover:text-white hover:bg-white/5 focus:ring-zinc-500",
    outline: "bg-transparent border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white focus:ring-zinc-500"
  };
  
  return (
    <button 
      type={type} 
      onClick={onClick} 
      disabled={disabled}
      className={`${baseStyle} ${sizes[size as keyof typeof sizes] || sizes.md} ${variants[variant as keyof typeof variants]} ${className}`}
    >
      {children}
    </button>
  );
};

export const Card = ({ children, className = '', noPadding = false, onClick }: any) => (
  <div 
    onClick={onClick}
    className={`bg-surface border border-border rounded-2xl shadow-card ${noPadding ? '' : 'p-5 md:p-6'} ${onClick ? 'cursor-pointer active:scale-[0.99] transition-transform' : ''} ${className}`}
  >
    {children}
  </div>
);

export const Input = ({ label, className = '', ...props }: any) => (
  <div className="mb-5 group w-full">
    {label && <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2 ml-1 group-focus-within:text-white transition-colors">{label}</label>}
    <input 
      // text-base prevents iOS zoom on focus
      className={`w-full px-5 py-3.5 rounded-xl bg-zinc-900/50 border border-zinc-800 text-base text-zinc-100 placeholder-zinc-600 focus:bg-zinc-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-300 hover:border-zinc-700 ${className}`}
      {...props}
    />
  </div>
);

export const Select = ({ label, options, className = '', ...props }: any) => (
  <div className="mb-5 group w-full">
    {label && <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2 ml-1 group-focus-within:text-white transition-colors">{label}</label>}
    <div className="relative">
      <select 
        className={`w-full px-5 py-3.5 rounded-xl bg-zinc-900/50 border border-zinc-800 text-base text-zinc-100 appearance-none focus:bg-zinc-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-300 cursor-pointer hover:border-zinc-700 ${className}`}
        {...props}
      >
        {options.map((opt: any) => (
          <option key={opt.value} value={opt.value} className="bg-zinc-900 text-zinc-100 py-2">{opt.label}</option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-zinc-500">
        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
      </div>
    </div>
  </div>
);

export const StatusBadge = ({ status, className = '' }: { status: OrderStatus, className?: string }) => {
  const styles = {
    [OrderStatus.PENDING]: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    [OrderStatus.DESIGNING]: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    [OrderStatus.NEEDS_REVIEW]: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    [OrderStatus.MILLING]: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    [OrderStatus.OUT_FOR_DELIVERY]: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    [OrderStatus.IN_TRY_IN]: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/20',
    [OrderStatus.READY]: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    [OrderStatus.DELIVERED]: 'bg-zinc-100/10 text-zinc-400 border-zinc-100/20',
    [OrderStatus.CANCELLED]: 'bg-red-500/10 text-red-400 border-red-500/20',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wider uppercase border whitespace-nowrap ${styles[status] || styles[OrderStatus.PENDING]} ${className}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

export const Checkbox = ({ checked, onChange }: { checked: boolean, onChange: (checked: boolean) => void }) => (
  <div 
    onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
    className={`w-6 h-6 md:w-5 md:h-5 rounded flex items-center justify-center cursor-pointer transition-all border ${checked ? 'bg-blue-600 border-blue-600' : 'bg-zinc-900 border-zinc-700 hover:border-zinc-500'}`}
  >
    {checked && <svg className="w-4 h-4 md:w-3.5 md:h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
  </div>
);

export const Badge = ({ count, className = "" }: { count: number, className?: string }) => {
  if (count === 0) return null;
  return (
    <div className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-zinc-900 ${className}`}>
      {count > 9 ? '9+' : count}
    </div>
  );
};
